import type { ReactNode } from "react";
import { useMemo } from "react";

import { Flex } from "@similarweb/ui-components/foundations";

import type { ChipItem } from "app/pages/sales-intelligence/common-components/filters-panel";
import { LimitedChipItems } from "app/pages/sales-intelligence/common-components/filters-panel";
import { MAX_CHIPS_GROUP_AMOUNT } from "app/pages/sales-intelligence/common-components/filters-panel/constants";
import type { IndustryNodeId } from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { createIndustryTreeViewModel } from "app/pages/sales-intelligence/modules/industry/view/industry-tree/factory";

import type IndustryTaxonomyFilterModel from "../../../models/IndustryTaxonomyFilter/IndustryTaxonomyFilterModel";

const NO_OP_SEARCH_HITS = () => [];

const IndustryTaxonomyFilterResult = ({
    value,
    filter,
    isExpanded,
    onFilterUpdate,
}: IFilterViewBaseProps<IndustryTaxonomyFilterModel>): ReactNode => {
    // Chips only need id→label resolution; the tree/search half of the view model is unused here.
    const viewModel = useMemo(
        () =>
            createIndustryTreeViewModel({
                taxonomy: filter.options,
                value: value?.values ?? [],
                searchHits: NO_OP_SEARCH_HITS,
            }),
        [filter.options, value?.values],
    );

    const chipItems: readonly ChipItem[] = useMemo(
        () => viewModel.chipItems().map((chip) => ({ id: chip.id, text: chip.label })),
        [viewModel],
    );

    if (!value?.values?.length) {
        return null;
    }

    const handleChipClose = (chipId: string) => {
        onFilterUpdate({ inclusion: value.inclusion, values: viewModel.removeValue(chipId as IndustryNodeId) });
    };

    return (
        <Flex flexWrap="wrap">
            <LimitedChipItems
                items={chipItems}
                onDelete={handleChipClose}
                maxItems={MAX_CHIPS_GROUP_AMOUNT}
                isTabExpanded={isExpanded}
            />
        </Flex>
    );
};

export default IndustryTaxonomyFilterResult;
